import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Clock, AlertTriangle } from "lucide-react";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { StatCard } from "../../components/shared/StatCard";
import { api } from "../../lib/api";

const TYPE_LABELS = { acompte: "Acompte 30 %", solde: "Solde", complement: "Complément", caution: "Caution (empreinte — hors CA)" };
const STATUS_LABELS = {
  en_attente: "En attente", paye: "Payé", echoue: "Échoué", expire: "Expirée", remplacee: "Remplacée",
  rembourse_partiel: "Remboursé partiellement", rembourse: "Remboursé",
  enregistre: "Enregistré (simulation historique)",
  empreinte_en_cours: "Empreinte en cours", empreinte_active: "Empreinte active", liberee: "Libérée",
  capturee: "Retenue totale", capturee_partielle: "Retenue partielle", derogee: "Dérogation admin",
  doublon_annule: "Doublon annulé", expiree: "Expirée",
};

export default function PaymentsPage() {
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const load = () => {
    api.get("/admin/payments/overview").then((r) => setOverview(r.data)).catch(() => setOverview(false));
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-payments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B15 — Paiements &amp; Échéances</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Paiements &amp; échéances</h1>
          <p className="mt-1 text-sm text-muted-foreground">Acompte 30 % à la confirmation, solde payable à tout moment (J-7 max), rappels automatiques J-10/J-7/J-3/J-1. Aucun remboursement automatique — décision administrative selon CGV.</p>
        </div>
      </div>

      {overview?.mode === "stripe_test" && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gold/40 bg-gold-light px-4 py-2.5 text-xs font-semibold text-deepgreen" data-testid="stripe-mode-banner">
          Stripe en mode TEST — aucune charge réelle ; la caution est une empreinte séparée, hors chiffre d'affaires
        </div>
      )}
      {overview?.mode === "stripe_live" && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-sage/40 bg-sage-light px-4 py-2.5 text-xs font-semibold text-deepgreen" data-testid="stripe-mode-banner">
          Paiements en production — transactions réelles ; la caution reste une empreinte séparée, hors chiffre d'affaires
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div data-testid="admin-payments-stat-collect"><StatCard icon={Clock} label="À encaisser" value={`${(overview?.a_encaisser ?? 0).toFixed(2)} €`} /></div>
        <div data-testid="admin-payments-stat-overdue"><StatCard icon={AlertTriangle} label="En retard (J-7 dépassé)" value={`${(overview?.en_retard ?? 0).toFixed(2)} €`} tone="alert" /></div>
        <div data-testid="admin-payments-stat-collected"><StatCard icon={CreditCard} label="Encaissé ce mois-ci" value={`${(overview?.encaisse_mois ?? 0).toFixed(2)} €`} hint="Suivi — pas de comptabilité" /></div>
      </div>

      <div className="card-ae mt-6 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="admin-payments-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">Réservation</th>
              <th className="px-5 py-3">Client</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3 text-right">Montant</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payments.map((p) => (
              <tr key={p.id} className="transition-colors hover:bg-cream/60" data-testid={`admin-payment-row-${p.id}`}>
                <td className="px-5 py-3.5 font-semibold text-deepgreen">
                  <Link to={`/admin/reservations/${p.reservation_id}`} className="hover:text-sage">{p.ref || "Voir la réservation"}</Link>
                </td>
                <td className="px-5 py-3.5">{p.customer_email || "—"}</td>
                <td className="px-5 py-3.5">{TYPE_LABELS[p.type] || p.type}</td>
                <td className="px-5 py-3.5 text-right font-semibold">
                  {(p.amount ?? 0).toFixed(2)} €
                  {p.refunded_amount > 0 && <span className="block text-[11px] font-normal text-red-600">−{p.refunded_amount.toFixed(2)} € remboursés</span>}
                </td>
                <td className="px-5 py-3.5 text-muted-foreground">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3.5"><StatusBadge status={STATUS_LABELS[p.status] || p.status} /></td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="admin-payments-empty">Aucune transaction pour le moment — les acomptes, soldes et cautions apparaîtront ici.</td></tr>
            )}
          </tbody>
        </table>
        <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
          Remboursements et cautions (capture/libération/dérogation) se pilotent depuis la fiche réservation, onglet « Paiements » — toujours avec motif et traçabilité. La caution reste hors chiffre d'affaires.
        </p>
      </div>
    </div>
  );
}
